import React from 'react';
import {Link} from 'react-router-dom';
import {DateTime} from 'luxon';

import FolderLayout from 'Components/HumanResources/StaffFolder/FolderLayout';
import {Card, SmallLink, SmallButton, Pill, Stat, Details, SimpleTable, plural} from 'Components/HumanResources/StaffFolder/Ui';
import {Loading, ActionsButton} from 'Components/Partials';
import CreateTimesheetModal from 'Components/HumanResources/Timesheets/CreateTimesheetModal';
import QueryTimesheetModal from 'Components/HumanResources/Timesheets/QueryTimesheetModal';

import {Modal, Toast} from 'Services';
import {getRecordActions} from 'Services/HrHelpers';
import UsersApi from 'Services/Api/Admin/HumanResources/Users';
import UserSettingsApi from 'Services/Api/Admin/HumanResources/UserSettings';
import AdminTimesheetsApi from 'Services/Api/Admin/HumanResources/Timesheets';

const FILTERS = [
    {key: 'all', label: 'All'},
    {key: 'not_submitted', label: 'Not submitted'},
    {key: 'awaiting', label: 'Submitted'},
    {key: 'queried', label: 'Queried'},
    {key: 'approved', label: 'Approved'},
];

/**
 * The status of an entry, as one of the filter keys.
 *
 * @param {object} entry
 * @return {string}
 */
const statusOf = (entry) => {
    if (entry.is_rejected) return 'declined';
    if (entry.query_resolved === false) return 'queried';
    if (entry.approved_by) return 'approved';
    if (entry.submitted) return entry.needs_approval ? 'awaiting' : 'submitted';
    return 'not_submitted';
};

const STATUS_PILLS = {
    declined: ['Declined', 'bad'],
    queried: ['Queried', 'bad'],
    approved: ['Approved', 'good'],
    awaiting: ['Submitted', 'warn'],
    submitted: ['Submitted', 'info'],
    not_submitted: ['Not submitted', 'neutral'],
};

/**
 * @param {object} value {iso_string}
 * @return {DateTime|null}
 */
const toDateTime = (value) => {
    const iso = typeof value === 'string' ? value : value?.iso_string;

    return iso ? DateTime.fromISO(iso) : null;
};

/**
 * Staff folder: Timesheets. One month of entries with what needs approving or answering.
 */
export default class Timesheets extends React.Component {
    /**
     * @var state
     */
    state = {
        month: DateTime.now().toFormat('yyyy-MM'),
        entries: null,
        summary: null,
        settings: null,
        filter: 'all',
        approving: false,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.load();

        AdminTimesheetsApi.on('updated', this.load);
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        AdminTimesheetsApi.removeListener('updated', this.load);
    };

    /**
     * @method load
     */
    load = async () => {
        const userId = this.props.match.params.user;
        const {month} = this.state;

        const [entries, summary, settings] = await Promise.all([
            AdminTimesheetsApi.get(null, {employee_id: userId, month: month + '-01', order_by: 'start_date_time', order: 'asc'}),
            UsersApi.getFolderTimesheets(userId, month),
            UserSettingsApi.get({user: userId}),
        ]);

        if (!entries.success || !summary.success) {
            return Toast.error();
        }

        this.setState({
            entries: entries.data.data,
            summary: summary.data.data,
            settings: settings.success ? settings.data.data : null,
        });
    };

    /**
     * The last 12 months, newest first.
     *
     * @method months
     * @return {array}
     */
    months = () => {
        return [...Array(12).keys()].map(i => {
            const month = DateTime.now().startOf('month').minus({months: i});

            return {value: month.toFormat('yyyy-MM'), label: month.toFormat('LLLL yyyy')};
        });
    };

    /**
     * @method add
     */
    add = () => {
        Modal.open({
            component: CreateTimesheetModal,
            props: {type: 'time_worked', admin: true, employee_id: this.props.match.params.user},
        });
    };

    /**
     * Approve every submitted entry that needs approving.
     *
     * @method approveAll
     */
    approveAll = async () => {
        const waiting = (this.state.entries ?? []).filter(entry => statusOf(entry) === 'awaiting' && entry.type !== 'holiday');

        this.setState({approving: true});

        for (const entry of waiting) {
            await AdminTimesheetsApi.patch({record: entry.id}, {...entry, approved: true});
        }

        this.setState({approving: false});
        Toast.success(`Approved ${plural(waiting.length, 'entry', 'entries')}.`);
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const userId = this.props.match.params.user;
        const {month, entries, summary, settings, filter, approving} = this.state;

        const all = entries ?? [];
        const counts = FILTERS.reduce((carry, item) => ({
            ...carry,
            [item.key]: item.key === 'all' ? all.length : all.filter(entry => statusOf(entry) === item.key).length,
        }), {});
        const shown = all.filter(entry => filter === 'all' || statusOf(entry) === filter);
        const queried = all.filter(entry => statusOf(entry) === 'queried');
        const approvable = all.filter(entry => statusOf(entry) === 'awaiting' && entry.type !== 'holiday').length;
        const yesNo = (value) => value ? 'Yes' : 'No';

        return (
            <FolderLayout userId={userId}>
                {({user}) => (!entries || !summary) ? <Loading/> : (
                    <div className="space-y-5">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                            <div className="flex flex-wrap items-end gap-4">
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="ts-month" className="text-sm font-bold text-gray-600">Month</label>
                                    <select
                                        id="ts-month"
                                        className="rounded-xl border-2 border-gray-300 py-2 px-3 bg-white"
                                        value={month}
                                        onChange={(event) => this.setState({month: event.target.value, entries: null}, this.load)}
                                    >
                                        {this.months().map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </div>
                                <SmallLink to={`/admin/human-resources/calendar/${userId}`}>Calendar view</SmallLink>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <SmallButton onClick={this.add}>Add entry</SmallButton>
                                {approvable > 0 && (
                                    <SmallButton primary disabled={approving} onClick={this.approveAll}>
                                        {approving ? 'Approving...' : `Approve ${approvable} submitted`}
                                    </SmallButton>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <Stat
                                label="Hours worked"
                                value={`${summary.hours}h ${summary.mins}m`}
                                note={plural(summary.entries, 'shift')}
                            />
                            <Stat
                                label="Waiting for you"
                                value={`${summary.to_approve} to approve`}
                                note={summary.queried ? `and ${plural(summary.queried, 'query', 'queries')} to answer` : null}
                                tone={summary.to_approve || summary.queried ? 'warn' : null}
                            />
                            <Stat label="Sleep-ins" value={summary.sleep_ins} />
                            <Stat label="Mileage" value={summary.mileage.toFixed(2)} note={plural(summary.mileage_entries, 'entry', 'entries')} />
                        </div>

                        {queried.map(entry => {
                            const start = toDateTime(entry.start_date_time);
                            const end = toDateTime(entry.end_date_time);

                            return (
                                <div key={entry.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-red-50 border border-red-200 p-4">
                                    <div>
                                        <div className="font-bold">
                                            {user.first_name} has queried {start?.toFormat('ccc d LLL, HH:mm')}{end ? ' to ' + end.toFormat('HH:mm') : ''}
                                        </div>
                                        <div className="text-sm text-gray-600">Open Reply to see their note and answer it.</div>
                                    </div>
                                    <div className="flex gap-3">
                                        <SmallButton onClick={() => Modal.open({component: CreateTimesheetModal, props: {record: entry, admin: true}})}>
                                            Edit entry
                                        </SmallButton>
                                        <SmallButton primary onClick={() => Modal.open({component: QueryTimesheetModal, props: {timesheet: entry, admin: true}})}>
                                            Reply
                                        </SmallButton>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="flex flex-wrap gap-2" role="group" aria-label="Show entries">
                            {FILTERS.map(item => (
                                <button
                                    key={item.key}
                                    type="button"
                                    aria-pressed={filter === item.key}
                                    onClick={() => this.setState({filter: item.key})}
                                    className={`rounded-full px-4 py-2 text-sm font-bold border-2
                                        ${filter === item.key ? 'bg-app-leading border-app-leading text-white' : 'bg-white border-gray-300'}`}
                                >
                                    {item.label} {counts[item.key]}
                                </button>
                            ))}
                        </div>

                        <Card>
                            <SimpleTable
                                headings={['Date', 'Time', 'Type', 'Hours', 'Service user', 'Sleep-in', 'Mileage', 'Status', '']}
                                empty="No entries for this month."
                                rows={shown.map(entry => {
                                    const start = toDateTime(entry.start_date_time);
                                    const end = toDateTime(entry.end_date_time);
                                    const [label, tone] = STATUS_PILLS[statusOf(entry)];

                                    return [
                                        <span className="font-bold whitespace-nowrap">{start?.toFormat('ccc d LLL')}</span>,
                                        entry.all_day ? 'All day' : `${start?.toFormat('HH:mm') ?? ''}${end ? '–' + end.toFormat('HH:mm') : ''}`,
                                        window.base.hr_timesheet_types?.[entry.type]?.label ?? entry.type,
                                        entry.hours ? `${entry.hours.hours}h ${entry.hours.mins}m` : '',
                                        entry.service_user?.full_name ?? '',
                                        entry.sleeps_in ? 'Yes' : '',
                                        entry.mileage > 0 ? Number(entry.mileage).toFixed(2) : '',
                                        <Pill tone={tone}>{label}</Pill>,
                                        <ActionsButton buttons={getRecordActions(entry, true)} layout="row" />,
                                    ];
                                })}
                            />
                        </Card>

                        <Card
                            title="Timesheet settings"
                            id="ts-set"
                            action={<Link to={`/admin/human-resources/settings/${userId}`} className="font-bold text-app-leading hover:underline">Edit</Link>}
                        >
                            {settings ? (
                                <Details items={[
                                    ['Shift needs approval', yesNo(settings.time_worked_needs_approval)],
                                    ['Cannot edit a submitted timesheet', yesNo(settings.user_cannot_edit_submitted_timesheet)],
                                ]}/>
                            ) : <p className="text-gray-600">No timesheet settings yet.</p>}
                        </Card>
                    </div>
                )}
            </FolderLayout>
        );
    }
}
